import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import Recording from './pages/Recording';
import OperatorFeeds from './pages/OperatorFeeds';
import Uploading from './pages/Uploading';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminAllFeeds from './pages/AdminAllFeeds';
import AdminFailedUploads from './pages/AdminFailedUploads';
import AdminUploading from './pages/AdminUploading';
import './App.css';

// VMS is SSO-only — there is no local login page. Access always arrives via
// ?qq_token=... from the QuirkyQode "my tools" portal (see AuthContext's SSO
// exchange). Anyone who isn't authenticated is sent back to that portal,
// exactly like the B2B Tool.
const QQ_PORTAL_URL = process.env.REACT_APP_QQ_PORTAL_URL || 'https://quirkyqode.com/my-tools';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.href = QQ_PORTAL_URL;
    }
  }, [loading, isAuthenticated]);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Protected Operator Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recording"
        element={
          <ProtectedRoute>
            <Recording />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feeds"
        element={
          <ProtectedRoute>
            <OperatorFeeds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uploading"
        element={
          <ProtectedRoute>
            <Uploading />
          </ProtectedRoute>
        }
      />

      {/* Protected Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feeds"
        element={
          <ProtectedRoute adminOnly>
            <AdminAllFeeds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/failed"
        element={
          <ProtectedRoute adminOnly>
            <AdminFailedUploads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/uploading"
        element={
          <ProtectedRoute adminOnly>
            <AdminUploading />
          </ProtectedRoute>
        }
      />

      {/* Default redirect — routed through ProtectedRoute so it waits on
          AuthContext's `loading` state before deciding where to go. A bare
          <Navigate> here would fire on first render and strip ?qq_token=...
          from the URL before the SSO exchange in AuthContext gets to read it. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <UploadProvider>
              <AppRoutes />
            </UploadProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
